import { tiposDeFotos } from "@/constants/tipoFoto";

export function getDataUrlEstimatedBytes(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return 0;
  const base64 = dataUrl.split(",")[1] || "";
  return Math.floor((base64.length * 3) / 4);
}

export function waitForRenderFrames(frames = 2, timeoutMs = 1000) {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "undefined") {
      setTimeout(resolve, 50);
      return;
    }

    // O navegador PAUSA o requestAnimationFrame quando a janela fica oculta
    // (protetor de tela, outra janela na frente, monitor desligado). Sem esta
    // rede de segurança, a exportação da prévia ficava aguardando um frame que
    // nunca chega e a tela de "Finalizar" travava para sempre.
    let finalizado = false;
    const concluir = () => {
      if (finalizado) return;
      finalizado = true;
      clearTimeout(temporizador);
      resolve();
    };

    const temporizador = setTimeout(concluir, timeoutMs);

    let remaining = frames;
    const step = () => {
      remaining -= 1;
      if (remaining <= 0) {
        concluir();
        return;
      }
      requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  });
}

export function isBlankLike(analise, bytes, minDataUrlBytes = 12000) {
  if (!analise) return true;
  return (
    analise.isInvalid ||
    bytes < minDataUrlBytes ||
    analise.opaqueRatio < 0.05 ||
    analise.whiteRatio > 0.6
  );
}

// A exportação não gerou uma imagem utilizável (vazia, transparente, pequena).
export function saidaQuebrada(analise, bytes, minDataUrlBytes = 12000) {
  return (
    !analise ||
    analise.isInvalid ||
    bytes < minDataUrlBytes ||
    analise.opaqueRatio < 0.05
  );
}

// Parte mínima da foto que precisa aparecer dentro do canvas.
const AREA_MINIMA_VISIVEL = 0.05;

// A foto não está no canvas do jeito que deveria: não há foto, a imagem dela
// ficou vazia (o navegador apagou) ou ela saiu quase toda para fora do canvas.
// Sem como inspecionar o canvas, considera que sumiu (comportamento antigo).
export function fotoDoCanvasSumiu(canvasInst) {
  if (!canvasInst || typeof canvasInst.getObjects !== "function") return true;
  const fotos = canvasInst
    .getObjects()
    .filter((objeto) => objeto.toObject && objeto.toObject().tipo === "foto");
  if (!fotos.length) return true;
  return fotos.some((foto) => {
    if (foto.visible === false || foto.opacity === 0) return true;
    if (fotoForaDoCanvas(foto, canvasInst)) return true;
    const elemento =
      typeof foto.getElement === "function" ? foto.getElement() : foto._element;
    if (!elemento) return true;
    const analise = analisarElemento(elemento);
    return (
      analise.isInvalid ||
      analise.opaqueRatio < 0.05 ||
      (analise.whiteRatio >= 0.99 && analise.lumStdDev < 3)
    );
  });
}

function fotoForaDoCanvas(foto, canvasInst) {
  if (
    typeof foto.getBoundingRect !== "function" ||
    typeof canvasInst.getWidth !== "function"
  )
    return false;
  const largura = canvasInst.getWidth();
  const altura = canvasInst.getHeight();
  const r = foto.getBoundingRect(true, true);
  const visivelX = Math.max(
    0,
    Math.min(r.left + r.width, largura) - Math.max(r.left, 0)
  );
  const visivelY = Math.max(
    0,
    Math.min(r.top + r.height, altura) - Math.max(r.top, 0)
  );
  return visivelX * visivelY < largura * altura * AREA_MINIMA_VISIVEL;
}

// Lado do canvas de amostragem usado nas análises de pixels.
const LADO_AMOSTRA = 128;

// Estatísticas de um buffer RGBA (ImageData.data).
export function analisarPixels(pixels) {
  if (!pixels || !pixels.length) return { isInvalid: true };

  const total = pixels.length / 4;
  let opaque = 0;
  let whiteOpaque = 0;
  let lumSum = 0;
  let lumSquaredSum = 0;

  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    const a = pixels[i + 3];

    if (a > 10) {
      opaque += 1;
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      lumSum += lum;
      lumSquaredSum += lum * lum;
      if (r > 245 && g > 245 && b > 245) {
        whiteOpaque += 1;
      }
    }
  }

  const opaqueRatio = total ? opaque / total : 0;
  const whiteRatio = opaque ? whiteOpaque / opaque : 1;
  const lumMean = opaque ? lumSum / opaque : 255;
  const variance = opaque ? lumSquaredSum / opaque - lumMean * lumMean : 0;
  const lumStdDev = Math.sqrt(Math.max(variance, 0));

  return {
    isInvalid: false,
    opaqueRatio,
    whiteRatio,
    lumStdDev,
  };
}

// Reduz `elemento` (img ou canvas) — ou só o `recorte` {x, y, w, h} dele —
// para o canvas de amostragem e analisa os pixels.
export function analisarElemento(elemento, recorte = null) {
  const sampleCanvas = document.createElement("canvas");
  sampleCanvas.width = LADO_AMOSTRA;
  sampleCanvas.height = LADO_AMOSTRA;
  const ctx = sampleCanvas.getContext("2d");
  if (!ctx) return { isInvalid: true };

  try {
    if (recorte) {
      ctx.drawImage(
        elemento,
        recorte.x,
        recorte.y,
        recorte.w,
        recorte.h,
        0,
        0,
        LADO_AMOSTRA,
        LADO_AMOSTRA
      );
    } else {
      ctx.drawImage(elemento, 0, 0, LADO_AMOSTRA, LADO_AMOSTRA);
    }
    const { data } = ctx.getImageData(0, 0, LADO_AMOSTRA, LADO_AMOSTRA);
    return analisarPixels(data);
  } catch (error) {
    return { isInvalid: true };
  }
}

export function analyzeImageDataUrl(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl || typeof dataUrl !== "string") {
      resolve({ isInvalid: true });
      return;
    }

    const image = new Image();
    image.onload = () => resolve(analisarElemento(image));
    image.onerror = () => resolve({ isInvalid: true });
    image.src = dataUrl;
  });
}

// A área da folha de impressão onde a foto foi desenhada ficou branca lisa
// enquanto a prévia dessa foto não era branca: a foto não entrou na folha.
// Sem a análise da prévia não dá para comparar, e a folha passa.
export function regiaoFicouVazia(analiseRegiao, analiseEsperada) {
  if (!analiseRegiao || analiseRegiao.isInvalid) return false;
  const vazia = analiseRegiao.whiteRatio >= 0.99 && analiseRegiao.lumStdDev < 3;
  if (!vazia) return false;
  if (!analiseEsperada || analiseEsperada.isInvalid) return false;
  return analiseEsperada.whiteRatio < 0.95;
}

export async function exportCanvasWithValidation({
  canvasInst,
  tipo,
  changedOrientation,
  typeIsOrientation,
  rotateFinalImage,
  maxAttempts = 3,
  minDataUrlBytes = 12000,
  // Chamada depois de uma tentativa que saiu branca, antes da próxima: só
  // desenhar de novo não traz de volta uma foto que o navegador apagou.
  recuperar = async () => {},
  options = {
    format: "png",
    quality: 1,
    multiplier: 2,
  },
}) {
  let tentativa = 1;
  let ultimaAnalise = null;
  let imagemDataUrl = "";

  let imagemDataUrlDisplay = "";

  while (tentativa <= maxAttempts) {
    canvasInst.renderAll();
    canvasInst.requestRenderAll();
    await waitForRenderFrames(2);

    const imagemDataUrlRaw = canvasInst.toDataURL(options);
    imagemDataUrlDisplay = imagemDataUrlRaw;
    imagemDataUrl = imagemDataUrlRaw;

    if (typeIsOrientation) {
      if (tipo === tiposDeFotos.NORMAL && !changedOrientation) {
        imagemDataUrl = await rotateFinalImage(imagemDataUrl);
      }
    }

    const bytes = getDataUrlEstimatedBytes(imagemDataUrl);
    const analise = await analyzeImageDataUrl(imagemDataUrl);
    ultimaAnalise = { ...analise, bytes, tentativa };

    // Muito branco nem sempre é foto perdida (fundo claro, moldura com miolo
    // branco). Só conta como branca se a saída quebrou ou se a foto do canvas
    // de fato sumiu.
    const blankLike =
      isBlankLike(analise, bytes, minDataUrlBytes) &&
      (saidaQuebrada(analise, bytes, minDataUrlBytes) ||
        fotoDoCanvasSumiu(canvasInst));

    if (!blankLike) {
      return {
        imagemDataUrl,
        imagemDataUrlDisplay,
        ultimaAnalise,
        tentativas: tentativa,
        comProblema: false,
      };
    }

    if (tentativa < maxAttempts) {
      try {
        await recuperar(tentativa);
      } catch (erro) {
        console.warn("Falha ao recuperar o canvas para exportar", erro);
      }
    }
    tentativa += 1;
    await waitForRenderFrames(2);
  }

  return {
    imagemDataUrl,
    imagemDataUrlDisplay,
    ultimaAnalise,
    tentativas: maxAttempts,
    comProblema: true,
  };
}
